// what we are willing to put in an img src: absolute http(s), protocol-relative
// and root-relative paths, and inline images. zoho hands these back in more than
// one shape, and anything stricter drops a perfectly good logo without a trace
const ALLOWED_SRC = /^(https?:\/\/|\/\/|\/(?!\/)|data:image\/)/i;

// which agency field to read for the background the logo sits on. the names are
// about the logo's own ink, so the dark artwork is the one for light backgrounds
export const LOGO_TONE_FIELDS = {
  onLight: 'Logo_URL_Dark',
  onDark: 'Logo_URL_Light'
};

export const LOGO_TONES = Object.keys(LOGO_TONE_FIELDS);

/**
 * turns a raw zoho logo url into a usable url, or an empty string when there is none
 * @param value - raw field value, possibly null, the string 'null', padded, or not a string
 */
export function normalizeLogoUrl(value) {
  if (typeof value !== 'string') return '';

  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed === 'null' || trimmed === 'undefined') return '';
  if (!ALLOWED_SRC.test(trimmed)) return '';

  return trimmed;
}

function otherTone(tone) {
  return tone === 'onDark' ? 'onLight' : 'onDark';
}

// zoho api names are not ours to control, so a casing difference on the field
// would otherwise drop the logo without a trace
function readAgencyField(agency, field) {
  if (!agency || typeof agency !== 'object') return undefined;
  if (field in agency) return agency[field];

  const wanted = field.toLowerCase();
  const match = Object.keys(agency).find(key => key.toLowerCase() === wanted);
  return match ? agency[match] : undefined;
}

/**
 * agency logo url for the background it sits on, '' when the agency has neither
 * artwork. an agency that uploaded only one of the two still gets its own brand
 * everywhere: the remaining artwork is reused rather than falling back to the
 * portal's own logo, which would show sunshine branding on an agency's portal
 * @param zohoData - state.user.zohoData
 * @param tone - 'onLight' for light backgrounds, 'onDark' for dark ones
 */
export function selectAgencyLogoUrl(zohoData, tone = 'onLight') {
  const agency = zohoData?.agencyData;
  const wanted = LOGO_TONE_FIELDS[tone] ? tone : 'onLight';

  const exact = normalizeLogoUrl(
    readAgencyField(agency, LOGO_TONE_FIELDS[wanted])
  );
  if (exact) return exact;

  return normalizeLogoUrl(
    readAgencyField(agency, LOGO_TONE_FIELDS[otherTone(wanted)])
  );
}

/**
 * true once a real agent profile is loaded in the user slice. an empty slice is
 * logged out, which is not the same as "this agency has no logo"
 * @param zohoData - state.user.zohoData
 */
export function hasLoadedAgent(zohoData) {
  return Boolean(zohoData?.id);
}

/**
 * picks which image to paint, skipping any url already known to be broken
 * @param src - preferred image, typically an agent photo, '' when there is none
 * @param brandSrc - the agency logo, or the bundled asset when there is no agency logo
 * @param fallbackSrc - the bundled asset, the last resort
 * @param failedSrcs - urls that already failed to load
 * @returns {{ resolved: string, usedPreferred: boolean }}
 */
export function resolveLogoSrc({ src, brandSrc, fallbackSrc, failedSrcs = [] }) {
  const usedPreferred = Boolean(src) && !failedSrcs.includes(src);
  const candidate = usedPreferred ? src : brandSrc;

  if (failedSrcs.includes(candidate)) {
    return { resolved: fallbackSrc, usedPreferred: false };
  }
  return { resolved: candidate, usedPreferred };
}
